import type { Graphics } from 'pixi.js';

/**
 * UNIFIED KEY LIGHT (Art Revision, Wave 1 — see docs/art/art-revision-2026-06-21.md, finding S1+S4).
 *
 * The procedural catalog used to light every object ad-hoc: a white band on the top edge here, nothing
 * there, no consistent direction. This module gives the WHOLE set ONE convention — a soft key light from
 * the TOP-LEFT — so furniture, props and clutter share a single sense of volume instead of reading as
 * flat fills. Every solid body gets a LIT sheen toward the light (top) and a faint AMBIENT-OCCLUSION band
 * away from it (bottom); glossy materials (metal, ceramic, glass, screens) get a sharper, brighter streak
 * than matte ones (wood, fabric).
 *
 * WHY STACKED FILLS, NOT FillGradient: a Pixi {@link import('pixi.js').FillGradient} with `textureSpace:
 * 'local'` mints a gradient TEXTURE per instance and must be manually destroyed or it leaks (Pixi docs);
 * with 100+ furniture instances that is real GPU memory + a re-skin leak risk (the team already fought a
 * WebGL-context-leak FPS decay). These helpers are allocation-light, themable, and built ONCE on map
 * build/re-skin — never per frame — matching the Performance-Engineer gate the rest of the render obeys.
 *
 * Footprint is UNCHANGED: each helper draws strictly within the rect/ellipse you pass, so it is a drop-in
 * for an existing `.roundRect(...).fill(base)` and the captured decor placement (kind + x/y/scale/args in
 * office-decor.generated.ts) is untouched — no recapture needed.
 */

/** Top-highlight alpha by material: matte (wood/fabric) soft; glossy (metal/ceramic/glass/screen) sharp. */
export const SHEEN = { matte: 0.14, gloss: 0.32 } as const;
/** Ambient-occlusion alpha for the shaded (bottom, away-from-key-light) edge of a body. */
export const AO = 0.16;

/**
 * A rounded-rect body with the unified top-left key light baked in: base fill → a lit sheen band along
 * the top → a faint AO band along the bottom. `gloss` gives metal/ceramic/screens a sharper top streak;
 * matte wood/fabric stay soft. Returns the same Graphics for chaining. Footprint == the rect you pass.
 */
export function bodyRect(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  base: number,
  gloss = false,
): Graphics {
  const sheen = gloss ? SHEEN.gloss : SHEEN.matte;
  const lit = Math.max(2, h * 0.24);
  const occ = Math.max(2, h * 0.2);
  g.roundRect(x, y, w, h, r).fill(base);
  g.roundRect(x, y, w, lit, r).fill({ color: 0xffffff, alpha: sheen });
  g.roundRect(x, y + h - occ, w, occ, r).fill({ color: 0x000000, alpha: AO });
  return g;
}

/**
 * An ellipse body with the same top-left key light: base → top sheen cap → bottom AO crescent. For round
 * tops (table tops, pot bellies, domes). The sheen/AO are smaller inset ellipses so the ramp reads round.
 */
export function bodyEllipse(
  g: Graphics,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  base: number,
  gloss = false,
): Graphics {
  const sheen = gloss ? SHEEN.gloss : SHEEN.matte;
  g.ellipse(cx, cy, rx, ry).fill(base);
  // Lit cap toward the light (upper-left), inset so it reads as a curved highlight not a full re-fill.
  g.ellipse(cx - rx * 0.22, cy - ry * 0.42, rx * 0.6, ry * 0.42).fill({ color: 0xffffff, alpha: sheen });
  // AO crescent on the lower edge, away from the light.
  g.ellipse(cx, cy + ry * 0.5, rx * 0.82, ry * 0.4).fill({ color: 0x000000, alpha: AO * 0.85 });
  return g;
}

/**
 * A single bright specular streak for glossy surfaces (a glass screen, a polished metal flank). A thin
 * diagonal-ish highlight bar near the top-left of a rect — the "this material is shiny" tell. Cheap.
 */
export function glossStreak(g: Graphics, x: number, y: number, w: number, h: number): Graphics {
  g.roundRect(x, y, w, h, h / 2).fill({ color: 0xffffff, alpha: 0.5 });
  return g;
}
